import React from 'react';
import _ from 'underscore';
import { marked } from 'marked';
import {
  colors,
  getForegroundColorForColor,
} from '@shoflo-rundown/cuesheet-data-helper';

const noMarkdown = 'no_markdown_shoflo';

/**
 * Generate a link that won't get focus when tabbing:
 *
 * Using onClick prevents a link from accepting focus. See https://github.com/SHOFLO/Issues/issues/2268
 *
 * @param link
 * @param content
 * @private
 */
const _generateOnClickAnchor = (link, content) =>
  `<a onClick="window.open('${link}')" target="_blank">${content}</a>`;

export default {
  getNodesWithLineBreaks(text, replaceNode) {
    replaceNode = replaceNode || React.createElement('br');
    var contentLineNodes = [],
      lineDelim = '\n',
      lines = text.split(lineDelim);

    lines.forEach((line, i) => {
      contentLineNodes.push(line);
      if (i === lines.length - 1) return;
      contentLineNodes.push(replaceNode);
    });

    return contentLineNodes;
  },

  /**
   * Get text that has line breaks
   */
  getTextWithLineBreaks(text) {
    return this.getNodesWithLineBreaks(text, '<br/>').join('');
  },

  getFormatStyles(format) {
    if (!format) return null;

    const style = {};

    if (format.get('backColor')) {
      style.backgroundColor = colors[format.get('backColor')];
      style.color = getForegroundColorForColor(format.get('backColor'));
    }

    if (format.get('uppercase') === true) {
      style.textTransform = 'uppercase';
    }

    return style;
  },

  /**
   * Get markdown options for renderer
   */
  _getMarkdownRendererOptions(customOpts) {
    let defaultOpts = {
      /* eslint-disable no-unused-vars */
      link: (link, title, text) => _generateOnClickAnchor(link, text),
      image: (link, title, text) =>
        _generateOnClickAnchor(link, `<img src="${link}" alt="${text}"/>`),
      heading: (text, number) => noMarkdown,
      list: (items, ordered) => noMarkdown,
      paragraph: (text) => text,
      strong: (text) => noMarkdown,
      em: (text) => noMarkdown,
      code: (text) => noMarkdown,
      codespan: (text) => noMarkdown,
      blockquote: (text) => noMarkdown,
      /* eslint-enable no-unused-vars */
    };

    let renderer = new marked.Renderer();

    let renderOpts = _.extend(defaultOpts, customOpts);

    _.each(renderOpts, (func, key) => {
      renderer[key] = func;
    });

    return { renderer, breaks: true };
  },

  /**
   * Get the markdown options for cells
   */
  getMarkdownOptions() {
    return this._getMarkdownRendererOptions();
  },

  /**
   * Get the printable markdown options for cells
   */
  getPrintableMarkdownOptions() {
    let opts = {
      link(link, title, text) {
        return text;
      },
    };

    return this._getMarkdownRendererOptions(opts);
  },

  /**
   * Get the printable markdown options for cells
   */
  getGuestPassMarkdownOptions() {
    return this._getMarkdownRendererOptions();
  },

  _getTextWithHTMLBreaks(text) {
    if (!text || typeof text.indexOf('\n') !== 'number') return text;

    var modified = '';
    var lineDelim = '\n';
    var lines = text.split(lineDelim);

    lines.forEach(function (line) {
      modified += line + '<br>';
    });

    return modified;
  },

  getFormattedMarkdownContent(markdownOptions, text) {
    if (!text || typeof text !== 'string') return text;

    let textWithBreaks = this._getTextWithHTMLBreaks(text);
    let markdown = marked(textWithBreaks, markdownOptions);

    let content = /no_markdown_shoflo/.test(markdown)
      ? '<span>' + textWithBreaks + '</span>'
      : markdown;

    return content;
  },
};
